/**
 * Utility functions for file operations
 */

/**
 * Check if a file is permanent (no expiration)
 * @param ttl - TTL timestamp from file metadata (null/undefined = permanent)
 * @returns True if file is permanent (does not expire)
 */
export const isPermanentFile = (ttl?: number | null): boolean => {
  return ttl === null || ttl === undefined;
};

/**
 * Calculate remaining retention days for a file (7-day retention policy)
 * @param uploadedAt - Upload timestamp (Unix seconds or ISO string)
 * @param ttl - TTL timestamp (null/undefined = permanent file)
 * @returns Number of days remaining until file deletion, or -1 for permanent files
 */
export const calculateRetentionDays = (uploadedAt: string | number, ttl?: number | null): number => {
  // Permanent files (no TTL) never expire
  if (isPermanentFile(ttl)) {
    return -1;
  }

  // If no TTL provided, assume 7 days (backwards compatibility)
  if (!ttl) {
    const uploadDate = typeof uploadedAt === 'number'
      ? new Date(uploadedAt * 1000)
      : new Date(uploadedAt);

    const now = new Date();
    const diffMs = now.getTime() - uploadDate.getTime();
    const daysPassed = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const retentionDays = 7 - daysPassed;
    return Math.max(0, retentionDays);
  }

  // Calculate days remaining from TTL timestamp
  const now = Math.floor(Date.now() / 1000); // Current time in Unix seconds
  const secondsRemaining = ttl - now;
  const daysRemaining = Math.ceil(secondsRemaining / (60 * 60 * 24));

  return Math.max(0, daysRemaining); // Never return negative
};

/**
 * Format retention text
 * @param daysRemaining - Number of days remaining (-1 for permanent files)
 * @returns Formatted text string
 */
export const formatRetentionText = (daysRemaining: number): string => {
  if (daysRemaining === -1) return 'Does not expire';
  if (daysRemaining === 0) return 'Expires today';
  if (daysRemaining === 1) return '1 day left';
  return `${daysRemaining} days left`;
};

/**
 * Calculate precise time remaining until file deletion
 * @param uploadedAt - Upload timestamp (Unix seconds or ISO string)
 * @param ttl - TTL timestamp (null/undefined = permanent file)
 * @returns Object with days, hours, and minutes remaining (-1 for permanent files)
 */
export const calculatePreciseTimeRemaining = (uploadedAt: string | number, ttl?: number | null): { days: number; hours: number; minutes: number; totalMs: number; isPermanent: boolean } => {
  // Permanent files never expire
  if (isPermanentFile(ttl)) {
    return { days: -1, hours: -1, minutes: -1, totalMs: -1, isPermanent: true };
  }

  // If no TTL provided, assume 7 days after upload (backwards compatibility)
  let expiryDate: Date;
  if (!ttl) {
    const uploadDate = typeof uploadedAt === 'number'
      ? new Date(uploadedAt * 1000)
      : new Date(uploadedAt);
    expiryDate = new Date(uploadDate.getTime() + (7 * 24 * 60 * 60 * 1000));
  } else {
    // Use TTL timestamp directly (TTL is in Unix seconds)
    expiryDate = new Date(ttl * 1000);
  }

  const now = new Date();
  const remainingMs = Math.max(0, expiryDate.getTime() - now.getTime());

  const days = Math.floor(remainingMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((remainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes, totalMs: remainingMs, isPermanent: false };
};

/**
 * Format precise expiry time for tooltip
 * @param uploadedAt - Upload timestamp (Unix seconds or ISO string)
 * @param ttl - TTL timestamp (null/undefined = permanent file)
 * @returns Formatted string like "5 days, 3 hours, 42 minutes" or "Does not expire"
 */
export const formatPreciseExpiryTime = (uploadedAt: string | number, ttl?: number | null): string => {
  const { days, hours, minutes, totalMs, isPermanent } = calculatePreciseTimeRemaining(uploadedAt, ttl);

  if (isPermanent) return 'Does not expire';
  if (totalMs === 0) return 'Expired';

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

  return parts.join(', ');
};

/**
 * Copy text to clipboard
 * @param text - Text to copy
 * @returns Promise that resolves when copy is successful
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  await navigator.clipboard.writeText(text);
};

/**
 * Generate shareable URL for a file
 * @param fileId - File ID
 * @returns Full shareable URL
 */
export const getShareableUrl = (fileId: string): string => {
  return `${window.location.origin}/player/${fileId}`;
};
